// Reference extraction from the references section (PRD §4b).
//
// Sprint-3 scope: parse entries into structured form (authors, year, title,
// journal, DOI, PMID). Sprint 4 resolves DOIs against Crossref and runs the
// retraction check.

import type { ManuscriptDocument } from '../pdf/types';
import { stableFindingId } from './finding-id';
import type { PatternFinding, ReferenceEntry } from './types';

const PMID_RE = /\bPMID:?\s*(\d{4,9})\b/i;

// Editorial Manager bundles append non-reference tail content after the
// bibliography (cover letter, "Author Page Only", repeated title, response
// to reviewers). Cut it so it isn't parsed as a bogus final reference.
const TAIL_MARKER_RE =
  /\n\s*(?:author page only|for reviewers? only|cover letter|response to reviewers?|highlights\b|graphical abstract|thank you for considering|powered by editori?al manager|conflicts? of interest statement|author agreement)\b/i;

function trimReferencesTail(refsText: string): string {
  // Only look past the first ~80 chars so the "References" head + ref 1 stay.
  const idx = refsText.slice(80).search(TAIL_MARKER_RE);
  return idx >= 0 ? refsText.slice(0, 80 + idx) : refsText;
}

/**
 * Extract & normalise a DOI from a reference. Editorial Manager PDFs lay DOIs
 * out with spaces around hyphens ("10.1007/978 - 3 - 031 - 21448 - 6_2") and
 * often end them with sentence punctuation ("10.3390/jcm12062178.") — both
 * made every Crossref lookup 404 (and surface as CORS noise). We grab the DOI
 * allowing those spaced hyphen fragments, collapse the layout whitespace, and
 * strip trailing punctuation/brackets so the lookup uses the real DOI.
 */
function normalizeDoi(s: string): string | null {
  const m = s.match(/10\.\d{4,9}\/[^\s]*(?:\s*-\s*[^\s]*)*/i);
  if (!m) return null;
  const doi = m[0]
    .replace(/\s+/g, '')
    .replace(/[.,;:>)\]’'"]+$/, '');
  return /^10\.\d{4,9}\/.{2,}$/.test(doi) ? doi : null;
}

/**
 * Split the references section into entries. Tries common patterns:
 *   - Numbered: "1. ", "1 ", "[1] "
 *   - Hanging: paragraph breaks
 */
function splitEntries(refsText: string): { raw: string; offset: number }[] {
  const entries: { raw: string; offset: number }[] = [];
  // Tolerate the Editorial Manager layout "1 ." (space before the period).
  const numberedRe = /(?:^|\n)\s*(?:\[(\d{1,4})\]|(\d{1,4})\s*\.)\s+/g;
  const indices: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = numberedRe.exec(refsText)) !== null) {
    indices.push(m.index + (m[0].startsWith('\n') ? 1 : 0));
  }
  if (indices.length >= 3) {
    for (let i = 0; i < indices.length; i++) {
      const start = indices[i];
      const end = i + 1 < indices.length ? indices[i + 1] : refsText.length;
      entries.push({ raw: refsText.slice(start, end).trim(), offset: start });
    }
    return entries;
  }
  // Fallback: split on blank lines.
  const blockRe = /[^\n]+(?:\n[^\n]+)*/g;
  let bm: RegExpExecArray | null;
  while ((bm = blockRe.exec(refsText)) !== null) {
    const trimmed = bm[0].trim();
    if (trimmed.length > 30) entries.push({ raw: trimmed, offset: bm.index });
  }
  return entries;
}

/**
 * Publication year. In Vancouver style the pub year is near the end (the
 * first 19xx/20xx is often inside the title), BUT web/book refs append an
 * access date — "[cited 2026 Jan 15]", "Accessed 2026" — which must NOT be
 * read as the publication year. Take the LAST year token that is not an
 * access/cited date; fall back to the last token overall.
 */
function lastYear(s: string): { value: number; index: number } | null {
  const re = /\b(?:19|20)\d{2}\b/g;
  let m: RegExpExecArray | null;
  let lastAny: RegExpExecArray | null = null;
  let lastPub: RegExpExecArray | null = null;
  while ((m = re.exec(s)) !== null) {
    lastAny = m;
    const before = s.slice(Math.max(0, m.index - 16), m.index);
    if (!/\b(cited|accessed?|updated|retrieved|viewed|downloaded)\b/i.test(before)) {
      lastPub = m;
    }
  }
  const chosen = lastPub ?? lastAny;
  return chosen ? { value: Number.parseInt(chosen[0], 10), index: chosen.index } : null;
}

function parseAuthors(entry: string): string[] {
  // Strip any leading citation-number prefix like "1.", "1 ." or "[1]".
  const stripped = entry.replace(/^\s*(?:\[\d+\]|\d+\s*\.)\s*/, '').trimStart();
  // Vancouver: "Surname II, Surname II[, et al]. Title. Journal. Year;…".
  // The author list ends at "et al" or the FIRST sentence-ending ". " — NOT
  // at the first year (the publication year is at the end, and a year inside
  // the title would otherwise drag title words in as bogus authors, causing
  // false Crossref first-author mismatches).
  const window = stripped.slice(0, 400);
  let end = window.length;
  const etAl = window.search(/\bet\s+al\b/i);
  if (etAl >= 0) end = Math.min(end, etAl);
  const dot = window.search(/\.\s/);
  if (dot >= 0) end = Math.min(end, dot);
  return window
    .slice(0, end)
    .split(/,|;|&| and /i)
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(
      (s) =>
        s.length >= 2 &&
        s.length <= 40 &&
        /^[A-Z]/.test(s) &&
        /[A-Za-z]/.test(s) &&
        !/^(?:https?:|doi:|available from|in:|\d+$)/i.test(s),
    )
    .slice(0, 20);
}

function parseEntry(raw: string, offset: number): ReferenceEntry {
  const numMatch = raw.match(/^\s*(?:\[(\d{1,4})\]|(\d{1,4})\s*\.)/);
  const number = numMatch ? Number.parseInt(numMatch[1] ?? numMatch[2], 10) : null;

  const doi = normalizeDoi(raw);
  const pmidMatch = raw.match(PMID_RE);
  const ym = lastYear(raw);

  const authors = parseAuthors(raw);

  // Title heuristic: the chunk between the year and the next period.
  let title = '';
  let journal: string | null = null;
  if (ym) {
    const afterYear = raw.slice(ym.index + 4).trimStart();
    // Pop leading period/dot/semicolon.
    const noLead = afterYear.replace(/^[.;:,]\s*/, '');
    const periodIdx = noLead.indexOf('. ');
    if (periodIdx > 0) {
      title = noLead.slice(0, periodIdx).trim();
      const rest = noLead.slice(periodIdx + 2);
      // Journal: text up to the next period or volume number.
      const journalMatch = rest.match(/^([^.\d]{2,80})/);
      journal = journalMatch ? journalMatch[1].trim().replace(/[.,;:]+$/, '') : null;
    } else {
      title = noLead.split('.')[0].trim();
    }
  }

  return {
    number,
    authors,
    year: ym ? ym.value : null,
    title,
    journal,
    doi,
    pmid: pmidMatch ? pmidMatch[1] : null,
    raw: raw.slice(0, 500),
    offset,
  };
}

export function extractReferences(doc: ManuscriptDocument): ReferenceEntry[] {
  const refsSpan = doc.sections.find((s) => s.section === 'references');
  if (!refsSpan) return [];
  const refsText = trimReferencesTail(doc.rawText.slice(refsSpan.start, refsSpan.end));
  const entries = splitEntries(refsText);
  return entries.map(({ raw, offset }) =>
    parseEntry(raw, refsSpan.start + offset),
  );
}

export function buildReferenceFindings(refs: ReferenceEntry[]): PatternFinding[] {
  const findings: PatternFinding[] = [];
  if (refs.length === 0) {
    return findings;
  }

  // Missing-DOI count: one summary finding rather than one per reference
  // (would flood the UI). DOIs that ARE present get resolved against Crossref
  // by the reference audit (apis/reference-audit.ts); this finding flags low
  // DOI coverage, which limits how much of the bibliography that automated
  // check can cover.
  const withDoi = refs.filter((r) => r.doi != null).length;
  const coverage = withDoi / refs.length;
  if (coverage < 0.7 && refs.length >= 10) {
    const findingText = `Only ${withDoi} of ${refs.length} references (${Math.round(coverage * 100)}%) include a DOI; references without one cannot be auto-verified against Crossref.`;
    findings.push({
      id: stableFindingId('references', findingText, refs[0].offset),
      category: 'references',
      severity: 'info',
      checklistItemId: null,
      findingText,
      evidencePassage: refs[0].raw.slice(0, 160),
      evidenceOffset: refs[0].offset,
      section: 'references',
      layer: 'pattern',
    });
  }

  // Implausibility: year missing.
  for (const r of refs) {
    if (r.year === null) {
      const findingText = `Reference ${r.number ?? '(unnumbered)'} has no parseable year. Verify formatting.`;
      findings.push({
        id: stableFindingId('references', findingText, r.offset),
        category: 'references',
        severity: 'amber',
        checklistItemId: null,
        findingText,
        evidencePassage: r.raw.slice(0, 160),
        evidenceOffset: r.offset,
        section: 'references',
        layer: 'pattern',
      });
    }
  }

  return findings;
}
